import pickBy from "lodash/pickBy";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { Textarea } from "../../../components/Textarea";
import useIsMobileViewport from "../../../hooks/useIsMobileViewport";
import { KanbanAutomationRule } from "../../../model/kanban/KanbanAutomationRule";
import { KanbanAutomationData } from "../../../model/kanban/KanbanAutomationData";
import { CreateKanbanAutomationRuleDto } from "../../../model/kanban/dto/CreateKanbanAutomationRuleDto";
import { KanbanColumnAutomationActionType } from "../../../model/kanban/types/KanbanColumnAutomationActionType.enum";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Checkbox, Radio, Switch } from "../../../utils/local-antd";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { kanbanAutomationActionMeta } from "../KanbanAutomationUtils";
import { KanbanAutomationRecipientsPicker } from "./KanbanAutomationRecipientsPicker";

const ActionRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  width: 100%;
`;

const ActionOption = styled(Radio)`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: ${Spacing.s};
  border: 1px solid ${({ theme }) => theme.darkSub};
  border-radius: ${Rounded.lg};
  transition: border-color 0.2s;

  &.ant-radio-wrapper-checked {
    border-color: ${({ theme }) => theme.main};
  }
`;

const DRAWER_SIZE_DESKTOP = "min(92vw, 720px)";
const DRAWER_SIZE_MOBILE = "100%";

const TASK_NAME_MAX_LENGTH = 256;
const TASK_DESCRIPTION_MAX_LENGTH = 1024;

type RuleFormValues = Required<
  Pick<
    CreateKanbanAutomationRuleDto,
    | "actionType"
    | "enabled"
    | "repeatable"
    | "recipientUserIds"
    | "assigneeUserIds"
  >
> & {
  /** The form holds every task setting as text; blanks are dropped on save. */
  data: Required<KanbanAutomationData>;
};

const newRuleValues: RuleFormValues = {
  actionType: KanbanColumnAutomationActionType.NOTIFICATION,
  enabled: true,
  repeatable: false,
  recipientUserIds: [],
  assigneeUserIds: [],
  data: { name: "", description: "" },
};

type KanbanAutomationRuleDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  /** The column a new rule is added to; the column of an edited rule. */
  columnId: string;
  columnName: string;
  /** The rule being edited, or `null` to configure a new one. */
  rule: KanbanAutomationRule | null;
};

/** Configure one automation rule - saving sends a single request for just it. */
export const KanbanAutomationRuleDrawer: React.FC<
  KanbanAutomationRuleDrawerProps
> = ({ open, onClose, columnId, columnName, rule }) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const isMobile = useIsMobileViewport();

  const { control, reset, handleSubmit, setError, watch } =
    useForm<RuleFormValues>({ defaultValues: newRuleValues });

  const actionType = watch("actionType");

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      rule
        ? {
            actionType: rule.actionType,
            enabled: rule.enabled,
            repeatable: rule.repeatable,
            recipientUserIds: rule.recipientUserIds || [],
            assigneeUserIds: rule.assigneeUserIds || [],
            data: {
              name: rule.data?.name ?? "",
              description: rule.data?.description ?? "",
            },
          }
        : newRuleValues,
    );
  }, [open, rule, reset]);

  const onSubmit = async (values: RuleFormValues) => {
    const fields = {
      actionType: values.actionType,
      enabled: values.enabled,
      repeatable: values.repeatable,
      // Recipients only exist on an email rule.
      ...(values.actionType === KanbanColumnAutomationActionType.NOTIFICATION
        ? { recipientUserIds: values.recipientUserIds }
        : {}),
      ...(values.actionType === KanbanColumnAutomationActionType.CREATE_TASK
        ? {
            data: pickBy({
              name: values.data.name.trim(),
              description: values.data.description.trim(),
            }),
            assigneeUserIds: values.assigneeUserIds,
          }
        : {}),
    };

    try {
      setLoading(true);

      if (rule) {
        await dispatch(
          kanbanActions.updateAutomationRule({
            userId,
            kanbanId: kanban!._id,
            ruleId: rule._id,
            ...fields,
          }),
        ).unwrap();
      } else {
        await dispatch(
          kanbanActions.createAutomationRule({
            userId,
            kanbanId: kanban!._id,
            columnId,
            ...fields,
          }),
        ).unwrap();
      }

      AtiraToast.success(
        rule
          ? t("kanban.automation.rule.update.success")
          : t("kanban.automation.rule.create.success"),
      );
      onClose();
    } catch (e: any) {
      onSetTypedCRUDErrors(e, setError);
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={isMobile ? DRAWER_SIZE_MOBILE : DRAWER_SIZE_DESKTOP}
      // Sits on top of the (2000) automation drawer that opened it.
      zIndex={2001}
      title={
        rule
          ? t("kanban.automation.rule.drawer.title.edit", { name: columnName })
          : t("kanban.automation.rule.drawer.title.create", {
              name: columnName,
            })
      }
    >
      <Flex flexDirection="column" gap="l" width={"100%"}>
        <Controller
          control={control}
          name="actionType"
          render={({ field: { value, onChange } }) => (
            <Flex flexDirection="column" gap="s">
              <Text fontWeight="bold">
                {t("kanban.automation.rule.form.actionType.label")}
              </Text>

              <ActionRadioGroup
                value={value}
                onChange={(e) => onChange(e.target.value)}
              >
                {Object.values(KanbanColumnAutomationActionType).map((type) => (
                  <ActionOption key={type} value={type}>
                    <Flex flexDirection="column" gap="xs">
                      <Flex alignItems="center" gap="s">
                        <AtiraIcon
                          icon={kanbanAutomationActionMeta[type].icon}
                          color="main"
                        />
                        <Text fontWeight="bold">
                          {t(kanbanAutomationActionMeta[type].titleKey)}
                        </Text>
                      </Flex>

                      <Text fontSize="s" color="gray">
                        {t(kanbanAutomationActionMeta[type].descriptionKey)}
                      </Text>
                    </Flex>
                  </ActionOption>
                ))}
              </ActionRadioGroup>
            </Flex>
          )}
        />

        {actionType === KanbanColumnAutomationActionType.CREATE_TASK ? (
          <Flex flexDirection="column" gap="m">
            <Controller
              control={control}
              name="data.name"
              rules={{
                maxLength: {
                  value: TASK_NAME_MAX_LENGTH,
                  message: t("kanban.automation.rule.form.maxLength", {
                    max: TASK_NAME_MAX_LENGTH,
                  }),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  title={t("kanban.automation.rule.form.taskName.label")}
                  // What the server names the task when this is left blank.
                  placeholder={t("kanban.automation.rule.taskName.default", {
                    column: columnName,
                  })}
                  maxLength={TASK_NAME_MAX_LENGTH}
                  value={value}
                  onChange={onChange}
                  valid={!error}
                  errorMessage={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="data.description"
              rules={{
                maxLength: {
                  value: TASK_DESCRIPTION_MAX_LENGTH,
                  message: t("kanban.automation.rule.form.maxLength", {
                    max: TASK_DESCRIPTION_MAX_LENGTH,
                  }),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Textarea
                  rows={4}
                  title={t("kanban.automation.rule.form.taskDescription.label")}
                  maxLength={TASK_DESCRIPTION_MAX_LENGTH}
                  value={value}
                  onChange={onChange}
                  valid={!error}
                  errorMessage={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="assigneeUserIds"
              render={({ field: { value, onChange } }) => (
                <Flex flexDirection="column" gap="s">
                  <Text>{t("kanban.automation.rule.form.assignees.title")}</Text>
                  <Text fontSize="s" color="gray">
                    {t("kanban.automation.rule.form.task.assigneesNote")}
                  </Text>
                  <KanbanAutomationRecipientsPicker
                    value={value}
                    onChange={onChange}
                  />
                </Flex>
              )}
            />
          </Flex>
        ) : null}

        <Controller
          control={control}
          name="enabled"
          // Whether recipients are required depends on this switch.
          rules={{ deps: ["recipientUserIds"] }}
          render={({ field: { value, onChange } }) => (
            <Flex alignItems="center" justifyContent="space-between" gap="m">
              <Flex flexDirection="column" gap="xs">
                <Text fontWeight="bold">
                  {t("kanban.automation.rule.form.enabled.label")}
                </Text>
                <Text fontSize="s" color="gray">
                  {t("kanban.automation.rule.form.enabled.description")}
                </Text>
              </Flex>

              <Switch checked={value} onChange={onChange} />
            </Flex>
          )}
        />

        <Controller
          control={control}
          name="repeatable"
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
            >
              {t("kanban.automation.rule.form.repeatable.label")}
            </Checkbox>
          )}
        />

        {actionType === KanbanColumnAutomationActionType.NOTIFICATION ? (
          <Controller
            control={control}
            name="recipientUserIds"
            rules={{
              validate: (value, { enabled }) =>
                !enabled ||
                value.length > 0 ||
                t("kanban.automation.rule.form.recipients.required"),
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Flex flexDirection="column" gap="s">
                <Text>{t("kanban.automation.rule.form.recipients.title")}</Text>
                <KanbanAutomationRecipientsPicker
                  value={value}
                  onChange={onChange}
                />

                {error ? (
                  <Text color="danger" fontSize="s">
                    {error.message}
                  </Text>
                ) : null}
              </Flex>
            )}
          />
        ) : null}

        <Button
          width="100%"
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          title={t("common.save")}
        />
      </Flex>
    </Drawer>
  );
};
